import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { leaderboardService } from "@/api/services/fantasy-leaderboard.service";

export const leaderboardKeys = {
  all: ["leaderboard"] as const,
  global: (limit: number, offset: number) =>
    [...leaderboardKeys.all, "global", limit, offset] as const,
  league: (id: number) => [...leaderboardKeys.all, "league", id] as const,
  stats: () => [...leaderboardKeys.all, "stats"] as const,
  topPlayers: () => [...leaderboardKeys.all, "top-players"] as const,
  topCaptains: () => [...leaderboardKeys.all, "top-captains"] as const,
  byPhase: (phase: string) => [...leaderboardKeys.all, "phase", phase] as const,
};

export function useGlobalLeaderboard(limit = 50, offset = 0) {
  return useQuery({
    queryKey: leaderboardKeys.global(limit, offset),
    queryFn: () => leaderboardService.getGlobal(limit, offset),
    refetchInterval: 60000,
  });
}

export function useLeagueLeaderboard(leagueId: number, limit = 100) {
  return useQuery({
    queryKey: leaderboardKeys.league(leagueId),
    queryFn: () => leaderboardService.getLeague(leagueId, limit),
    enabled: !!leagueId,
  });
}

export function useLeaderboardStats() {
  return useQuery({
    queryKey: leaderboardKeys.stats(),
    queryFn: leaderboardService.getStats,
    refetchInterval: 45000,
  });
}

export function useTopPlayers(limit = 10) {
  return useQuery({
    queryKey: leaderboardKeys.topPlayers(),
    queryFn: () => leaderboardService.getTopPlayers(limit),
  });
}

export function useTopCaptains(limit = 10) {
  return useQuery({
    queryKey: leaderboardKeys.topCaptains(),
    queryFn: () => leaderboardService.getTopCaptains(limit),
  });
}

export function useInvalidateCache() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: leaderboardService.invalidateCache,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaderboardKeys.all });
    },
  });
}

export function useLeaderboardByGameweek(gameweekId: number | null) {
  return useQuery({
    queryKey: [...leaderboardKeys.all, "gameweek", gameweekId] as const,
    queryFn: () => leaderboardService.getLeaderboardByGameweek(gameweekId!),
    enabled: !!gameweekId,
    refetchInterval: 45000,
  });
}